import React, { useState } from 'react'
import { Box, Center, Spinner, Text } from '@chakra-ui/react'

import { ClinicFieldsFragment } from '~graphql/generated/graphql'
import FormOption from './option'
import { PageSizeType } from '~config/constants'
import Pagination from '~components/shared/pagination'
import SharedStack from '~components/shared/wrappers/shared-stack'
import PageActionBar from '~components/shared/page-layout/action-bar'
import SharedSectionHeader from '~components/shared/texts/shared-section-header'
import { useFormQueries } from '~components/engagement/forms/forms-table/use-queries'
import { useDebounce } from '~utils/hooks/use-debounce'
import MissingText from '~components/shared/texts/missing-text'
import { usePageControls } from '~utils/hooks/use-page-controls'
import generatePublicFormLink from '~utils/helpers'

interface Props {
    clinic: Pick<ClinicFieldsFragment, 'id'> | null | undefined
    selectedLink: string | undefined
    onSelectLink: (link: string | undefined) => void
    /** Hides the section header when rendered on its own subpage. */
    hideHeader?: boolean
}

export default function FormsList({
    clinic,
    selectedLink,
    onSelectLink,
    hideHeader = false,
}: Props) {
    const [query, setQuery] = useState('')
    const { page, setPage, pageSize } = usePageControls(undefined, query)
    const debouncedQuery = useDebounce(query, 500)

    const {
        results: formOptions,
        count,
        loading,
    } = useFormQueries(
        clinic ? [clinic.id] : [],
        false,
        page,
        pageSize,
        debouncedQuery,
    )

    return (
        <SharedStack spacing={4}>
            <Box w="full">
                {!hideHeader && (
                    <SharedSectionHeader>Select a form</SharedSectionHeader>
                )}
                <PageActionBar
                    search={{
                        placeholder: 'Search forms by name...',
                        query,
                        setQuery,
                    }}
                    gridOverrides={{
                        mt: 2,
                        w: 'full',
                    }}
                />
            </Box>
            {loading && <Spinner size="sm" />}
            {!loading && formOptions.length === 0 && (
                <MissingText>No forms found.</MissingText>
            )}
            {!loading &&
                formOptions.map(form => {
                    const formUrl = generatePublicFormLink(form.id)
                    return (
                        <FormOption
                            key={form.id}
                            form={form}
                            isSelected={selectedLink === formUrl}
                            onClick={() => {
                                selectedLink === formUrl
                                    ? onSelectLink(undefined)
                                    : onSelectLink(formUrl)
                            }}
                            query={debouncedQuery}
                        />
                    )
                })}
            {count > pageSize && (
                <Center w="full">
                    <Pagination
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize as PageSizeType}
                        count={count}
                    />
                </Center>
            )}
        </SharedStack>
    )
}
